/**
 * QualificationSWOT.jsx — SWOT + Risk + Decision Matrix for the
 * "Qualification" stage of the enquiry funnel.
 *
 * Aligned with user's "SWOT Analysis - Ellora EPC.xlsx" template:
 *   • SWOT lists (Strengths / Weaknesses / Opportunities / Threats)
 *   • Overall Risk Score (5 parameters · 1–5 each)
 *   • Tender Decision Matrix (9 parameters with weights · 1–5 score →
 *     auto-weighted total → GO / CONDITIONAL GO / NO-GO)
 *   • Final Recommendation textarea
 *
 * Persists into the enquiry document under `qualification: { … }`.
 */
import React, { useMemo, useState } from 'react';
import { Plus, X, ShieldAlert, TrendingUp, AlertOctagon, Sparkles, ClipboardCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost } from '../../lib/api';
import { computeQualificationVerdict, verdictChipClass } from '../../lib/qualification';

const RISK_PARAMS = [
  { k: 'financial',     label: 'Financial Risk' },
  { k: 'technical',     label: 'Technical Risk' },
  { k: 'contractual',   label: 'Contractual Risk' },
  { k: 'execution',     label: 'Execution Difficulty' },
  { k: 'cashflow',      label: 'Cashflow Risk' },
];

const DECISION_PARAMS = [
  { k: 'technical_fit',         label: 'Technical Fit',                weight: 15 },
  { k: 'financial_feasibility', label: 'Financial Feasibility',        weight: 15 },
  { k: 'past_experience',       label: 'Past Experience Match',        weight: 10 },
  { k: 'competition',           label: 'Competition Intensity',        weight: 10 },
  { k: 'resource_availability', label: 'Resource Availability',        weight: 10 },
  { k: 'execution_risk',        label: 'Execution Risk',               weight: 15 },
  { k: 'cashflow_requirement',  label: 'Cashflow Requirement',         weight: 10 },
  { k: 'strategic',             label: 'Strategic Importance',         weight: 10 },
  { k: 'client_relationship',   label: 'Client Relationship Strength', weight:  5 },
];
// Sum of weights = 100. 1–5 score × weight%/100 → weighted score out of 5 each → max sum = 5 × 100 = 500.
// Normalise to a /100 score: (sum / 5) → 0-100. (5 × 100 / 5 = 100.)

const lbl = 'text-[11px] uppercase tracking-widest text-slate-500 font-display font-bold';

const SWOTBucket = ({ title, color, accent, points, onAdd, onChange, onRemove, testid, placeholder }) => (
  <div className={`border ${accent} rounded-md p-4 ${color}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className={`text-xs font-display font-bold uppercase tracking-widest ${accent.replace('border-', 'text-')}`}>{title}</div>
      <button type="button" onClick={onAdd} data-testid={`${testid}-add`} className="text-[11px] font-display font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1"><Plus size={11} /> Add</button>
    </div>
    {(points || []).length === 0 && (
      <div className="text-[11px] text-slate-400 italic py-2">No points yet. Click "+ Add" to record.</div>
    )}
    <div className="space-y-1.5">
      {(points || []).map((p, i) => (
        <div key={i} className="flex gap-1.5 items-start bg-white border border-slate-200 rounded p-1.5">
          <span className="text-[10px] tabular-nums text-slate-400 pt-1.5 w-5 text-right">{i + 1}.</span>
          <input
            value={p}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder={placeholder}
            className="flex-1 px-2 py-1 text-xs bg-transparent border-0 focus:outline-none"
            data-testid={`${testid}-row-${i}`}
          />
          <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-rose-500 px-1"><X size={12} /></button>
        </div>
      ))}
    </div>
  </div>
);

export default function QualificationSWOT({ qualification, onChange, enquiryId, ensureSaved }) {
  const q = qualification || {};
  const [aiBusy, setAiBusy] = useState(false);

  const runAI = async (overwrite = false) => {
    // Iter 137 — resolve the target enquiry id through the parent's atomic
    // ensureSaved() so a user who clicks "AI Draft" before the draft auto-save
    // completes does not start a second POST /enquiries.
    let targetId = enquiryId;
    if (!targetId && typeof ensureSaved === 'function') {
      targetId = await ensureSaved();
    }
    if (!targetId) {
      toast.error('Save the enquiry first so AI can read its uploaded documents.');
      return;
    }
    if (overwrite && !window.confirm('Overwrite existing SWOT / Risk / Decision values with AI draft? Empty fields will be filled regardless.')) return;
    setAiBusy(true);
    try {
      const r = await apiPost(`/enquiries/${targetId}/ai-qualification`, { overwrite });
      onChange(r.qualification || {});
      toast.success(overwrite ? 'AI re-drafted SWOT, risks & decision matrix.' : 'AI filled blank SWOT / Risk / Decision fields.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed. Upload tender docs first.');
    } finally {
      setAiBusy(false);
    }
  };

  // SWOT mutation helpers
  const _setList = (bucket, list) => onChange({ ...q, [bucket]: list });
  const _addPoint = (bucket) => _setList(bucket, [...(q[bucket] || []), '']);
  const _updPoint = (bucket, i, v) => _setList(bucket, (q[bucket] || []).map((p, idx) => idx === i ? v : p));
  const _delPoint = (bucket, i) => _setList(bucket, (q[bucket] || []).filter((_, idx) => idx !== i));

  // Risk scoring
  const risk = q.risk_scores || {};
  const setRisk = (k, field, val) => onChange({ ...q, risk_scores: { ...risk, [k]: { ...(risk[k] || {}), [field]: val } } });
  const riskAvg = useMemo(() => {
    const vals = RISK_PARAMS.map(p => Number(risk[p.k]?.score) || 0).filter(v => v > 0);
    return vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
  }, [risk]);
  const riskLabel = riskAvg >= 4 ? 'High'
                   : riskAvg >= 3 ? 'Moderate–High'
                   : riskAvg >= 2 ? 'Moderate'
                   : riskAvg >= 1 ? 'Low'
                                  : '—';
  const riskColor = riskAvg >= 4 ? 'text-rose-700 bg-rose-50 border-rose-200'
                  : riskAvg >= 3 ? 'text-orange-700 bg-orange-50 border-orange-200'
                  : riskAvg >= 2 ? 'text-amber-700 bg-amber-50 border-amber-200'
                  : riskAvg >= 1 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                  : 'text-slate-500 bg-slate-50 border-slate-200';

  // Decision matrix
  const dm = q.decision_matrix || {};
  const setDM = (k, field, val) => onChange({ ...q, decision_matrix: { ...dm, [k]: { ...(dm[k] || {}), [field]: val } } });
  const { score: dmNormalised, verdict } = useMemo(() => computeQualificationVerdict(q), [q]);
  const decision = verdict || '—';
  const decisionColor = verdict ? verdictChipClass(verdict) : 'bg-slate-300 text-slate-700';

  return (
    <div className="space-y-5">
      {/* AI banner */}
      <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-[#3b4872]/10 to-transparent border border-[#3b4872]/30 rounded-md p-3">
        <div className="flex items-center gap-2 text-xs text-slate-700">
          <Sparkles size={14} className="text-[#3b4872]" />
          <span><b className="font-display font-bold">Ellora AI</b> can read the uploaded tender documents and draft the SWOT, risk scores and Go/No-Go matrix.</span>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => runAI(false)}
            disabled={aiBusy || !enquiryId}
            data-testid="qual-ai-fill-blanks"
            title={enquiryId ? 'Fill only blank SWOT / Risk / Decision fields' : 'Save the enquiry first'}
            className="text-[11px] font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-[#3b4872] text-white rounded hover:bg-[#2c3658] disabled:opacity-50 flex items-center gap-1.5"
          >
            {aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            {aiBusy ? 'Drafting…' : 'AI Fill Blanks'}
          </button>
          <button
            type="button"
            onClick={() => runAI(true)}
            disabled={aiBusy || !enquiryId}
            data-testid="qual-ai-overwrite"
            title="Re-draft everything (overwrites your edits)"
            className="text-[11px] font-display font-bold uppercase tracking-wider px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded hover:bg-[#3b4872]/5 disabled:opacity-50"
          >
            Re-draft
          </button>
        </div>
      </div>

      {/* SWOT MATRIX */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} className="text-[#3b4872]" />
          <div className="font-display font-bold text-sm text-slate-900">SWOT Matrix</div>
          <span className="text-[11px] text-slate-500">— record points for each quadrant.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SWOTBucket
            title="Strengths (Internal Positive)"
            color="bg-emerald-50/40"
            accent="border-emerald-300"
            points={q.strengths}
            onAdd={() => _addPoint('strengths')}
            onChange={(i, v) => _updPoint('strengths', i, v)}
            onRemove={(i) => _delPoint('strengths', i)}
            placeholder="e.g. Strong technical team, in-house batching plant"
            testid="swot-strengths"
          />
          <SWOTBucket
            title="Weaknesses (Internal Negative)"
            color="bg-amber-50/40"
            accent="border-amber-300"
            points={q.weaknesses}
            onAdd={() => _addPoint('weaknesses')}
            onChange={(i, v) => _updPoint('weaknesses', i, v)}
            onRemove={(i) => _delPoint('weaknesses', i)}
            placeholder="e.g. Limited past experience in this specific sector"
            testid="swot-weaknesses"
          />
          <SWOTBucket
            title="Opportunities (External Positive)"
            color="bg-sky-50/40"
            accent="border-sky-300"
            points={q.opportunities}
            onAdd={() => _addPoint('opportunities')}
            onChange={(i, v) => _updPoint('opportunities', i, v)}
            onRemove={(i) => _delPoint('opportunities', i)}
            placeholder="e.g. Growing market demand for similar projects"
            testid="swot-opportunities"
          />
          <SWOTBucket
            title="Threats (External Negative)"
            color="bg-rose-50/40"
            accent="border-rose-300"
            points={q.threats}
            onAdd={() => _addPoint('threats')}
            onChange={(i, v) => _updPoint('threats', i, v)}
            onRemove={(i) => _delPoint('threats', i)}
            placeholder="e.g. Intense competition from established players"
            testid="swot-threats"
          />
        </div>
      </div>

      {/* RISK SCORE */}
      <div className="border border-slate-200 rounded-md p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert size={15} className="text-rose-600" />
            <div className="font-display font-bold text-sm text-slate-900">Overall Risk Score</div>
            <span className="text-[11px] text-slate-500">— Score 1 = Very Low · 5 = Very High</span>
          </div>
          <span className={`px-3 py-1 text-xs font-display font-bold uppercase tracking-wide rounded-sm border ${riskColor}`} data-testid="qual-risk-overall">
            Overall: {riskAvg ? riskAvg.toFixed(1) : '—'} · {riskLabel}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-display border-b border-slate-200">
                <th className="py-2 pr-3 w-1/3">Risk Parameter</th>
                <th className="py-2 pr-3 w-24">Score (1–5)</th>
                <th className="py-2">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {RISK_PARAMS.map((p) => (
                <tr key={p.k} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-display font-semibold text-slate-700">{p.label}</td>
                  <td className="py-2 pr-3">
                    <select
                      value={risk[p.k]?.score ?? ''}
                      onChange={(e) => setRisk(p.k, 'score', e.target.value === '' ? null : Number(e.target.value))}
                      data-testid={`qual-risk-${p.k}`}
                      className="w-20 px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
                    >
                      <option value="">—</option>
                      {[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="py-2">
                    <input
                      value={risk[p.k]?.remarks || ''}
                      onChange={(e) => setRisk(p.k, 'remarks', e.target.value)}
                      placeholder="Optional"
                      data-testid={`qual-risk-${p.k}-remarks`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DECISION MATRIX */}
      <div className="border border-slate-200 rounded-md p-4 bg-white">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={15} className="text-[#3b4872]" />
            <div className="font-display font-bold text-sm text-slate-900">Tender Decision Matrix — Go / No-Go</div>
            <span className="text-[11px] text-slate-500">— Score 1–5 each parameter. Total auto-scales to /100.</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500">Total: <b className="tabular-nums text-slate-900" data-testid="qual-decision-total">{dmNormalised.toFixed(1)}/100</b></span>
            <span className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest rounded-sm ${decisionColor}`} data-testid="qual-decision-verdict">
              {decision}
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-display border-b border-slate-200">
                <th className="py-2 pr-3 w-1/3">Parameter</th>
                <th className="py-2 pr-3 w-20">Weight</th>
                <th className="py-2 pr-3 w-20">Score</th>
                <th className="py-2 pr-3 w-28">Weighted</th>
                <th className="py-2">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {DECISION_PARAMS.map((p) => {
                const score = Number(dm[p.k]?.score) || 0;
                const weighted = (score * p.weight) / 5;  // normalise so max = weight
                return (
                  <tr key={p.k} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-display font-semibold text-slate-700">{p.label}</td>
                    <td className="py-2 pr-3 text-slate-500 tabular-nums">{p.weight}%</td>
                    <td className="py-2 pr-3">
                      <select
                        value={dm[p.k]?.score ?? ''}
                        onChange={(e) => setDM(p.k, 'score', e.target.value === '' ? null : Number(e.target.value))}
                        data-testid={`qual-dm-${p.k}`}
                        className="w-16 px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
                      >
                        <option value="">—</option>
                        {[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-3 tabular-nums font-display font-bold text-[#3b4872]">{weighted.toFixed(1)}</td>
                    <td className="py-2">
                      <input
                        value={dm[p.k]?.remarks || ''}
                        onChange={(e) => setDM(p.k, 'remarks', e.target.value)}
                        placeholder="Optional"
                        data-testid={`qual-dm-${p.k}-remarks`}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
            <div className="font-display font-bold text-emerald-700">GO</div>
            <div className="text-emerald-600">Score &gt; 75</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
            <div className="font-display font-bold text-amber-700">CONDITIONAL GO</div>
            <div className="text-amber-600">Score 50–75</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded p-2 text-center">
            <div className="font-display font-bold text-rose-700">NO-GO</div>
            <div className="text-rose-600">Score &lt; 50</div>
          </div>
        </div>
      </div>

      {/* FINAL RECOMMENDATION */}
      <div className="border border-slate-200 rounded-md p-4 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <ClipboardCheck size={15} className="text-[#3b4872]" />
          <div className="font-display font-bold text-sm text-slate-900">Final Recommendation</div>
        </div>
        <textarea
          rows={3}
          value={q.final_recommendation || ''}
          onChange={(e) => onChange({ ...q, final_recommendation: e.target.value })}
          placeholder="Summarise the qualification decision and any conditions. e.g. CONDITIONAL GO — proceed if EMD relaxed and JV partner identified for MEP scope."
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:border-[#3b4872] focus:outline-none"
          data-testid="qual-final-rec"
        />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className={lbl}>Decided By</label>
            <input value={q.decided_by || ''} onChange={(e) => onChange({ ...q, decided_by: e.target.value })}
              placeholder="Name" data-testid="qual-decided-by" className="w-full px-3 py-2 border border-slate-300 rounded text-sm mt-1" />
          </div>
          <div>
            <label className={lbl}>Decision Date</label>
            <input type="date" value={q.decided_on || ''} onChange={(e) => onChange({ ...q, decided_on: e.target.value })}
              data-testid="qual-decided-on" className="w-full px-3 py-2 border border-slate-300 rounded text-sm mt-1" />
          </div>
        </div>
      </div>

      {!!(q.strengths || q.weaknesses || q.opportunities || q.threats || decision !== '—') && (
        <div className="text-[11px] text-slate-500 flex items-start gap-1.5 bg-slate-50 border border-slate-200 rounded p-2">
          <AlertOctagon size={13} className="text-slate-400 shrink-0 mt-px" />
          Qualification snapshot persists with the enquiry. Decision verdict + total score are surfaced on the Enquiry list and used by the Tender Decision Dashboard.
        </div>
      )}
    </div>
  );
}
